import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CreditCard, Check, X, AlertTriangle, Undo2 } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { Badge } from '@/components/ui/Badge';
import { DataTable, Column } from '@/components/ui/DataTable';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { useBillingEvents, useRefund } from '@/hooks/useBilling';
import type { BillingEvent } from '@/types/admin.types';

const formatCurrency = (amount: number, currency: string = 'BRL') => {
  return new Intl.NumberFormat(currency === 'BRL' ? 'pt-BR' : 'en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(amount);
};

const getEventIcon = (type: string) => {
  switch (type) {
    case 'payment_succeeded':
      return <Check className="w-4 h-4 text-green-500" />;
    case 'payment_failed':
      return <X className="w-4 h-4 text-red-500" />;
    case 'subscription_created':
    case 'subscription_updated':
      return <CreditCard className="w-4 h-4 text-blue-500" />;
    case 'subscription_canceled':
      return <X className="w-4 h-4 text-orange-500" />;
    case 'refund':
      return <Undo2 className="w-4 h-4 text-purple-500" />;
    default:
      return <AlertTriangle className="w-4 h-4 text-gray-500" />;
  }
};

const getEventLabel = (type: string) => {
  switch (type) {
    case 'payment_succeeded':
      return 'Pagamento Aprovado';
    case 'payment_failed':
      return 'Pagamento Falhou';
    case 'subscription_created':
      return 'Assinatura Criada';
    case 'subscription_updated':
      return 'Assinatura Atualizada';
    case 'subscription_canceled':
      return 'Assinatura Cancelada';
    case 'refund':
      return 'Reembolso';
    default:
      return type;
  }
};

const getEventBadgeVariant = (type: string): 'success' | 'danger' | 'warning' | 'primary' | 'info' | 'default' => {
  switch (type) {
    case 'payment_succeeded':
      return 'success';
    case 'payment_failed':
      return 'danger';
    case 'subscription_created':
      return 'primary';
    case 'subscription_updated':
      return 'info';
    case 'subscription_canceled':
      return 'warning';
    case 'refund':
      return 'default';
    default:
      return 'default';
  }
};

export const BillingEventsPage: React.FC = () => {
  const navigate = useNavigate();
  const [typeFilter, setTypeFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<BillingEvent | null>(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');

  const { data, isLoading } = useBillingEvents({
    page,
    limit: 20,
    type: typeFilter !== 'all' ? typeFilter : undefined,
  });

  const refundMutation = useRefund();

  const handleRefundClick = (event: BillingEvent) => {
    setSelectedEvent(event);
    setRefundAmount(event.amount.toString());
    setRefundReason('');
    setShowRefundModal(true);
  };

  const handleRefund = async () => {
    if (!selectedEvent || !refundAmount) return;

    await refundMutation.mutateAsync({
      paymentIntentId: selectedEvent.stripePaymentIntentId || '',
      amount: parseFloat(refundAmount),
      reason: refundReason,
    });

    setShowRefundModal(false);
    setSelectedEvent(null);
  };

  const columns: Column<BillingEvent>[] = [
    {
      key: 'type',
      label: 'Evento',
      render: (event) => (
        <div className="flex items-center gap-2">
          {getEventIcon(event.type)}
          <Badge variant={getEventBadgeVariant(event.type)}>
            {getEventLabel(event.type)}
          </Badge>
        </div>
      ),
    },
    {
      key: 'user',
      label: 'Usuario',
      render: (event) => (
        <div>
          <p className="font-medium text-gray-900 dark:text-white">{event.userName}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">{event.userEmail || event.userPhone}</p>
        </div>
      ),
    },
    {
      key: 'amount',
      label: 'Valor',
      align: 'right',
      render: (event) => (
        <span className={`font-semibold ${event.type === 'refund' ? 'text-red-600 dark:text-red-400' : ''}`}>
          {event.type === 'refund' ? '-' : ''}{formatCurrency(event.amount, event.currency)}
        </span>
      ),
    },
    {
      key: 'plan',
      label: 'Plano',
      render: (event) => event.planName || '-',
    },
    {
      key: 'createdAt',
      label: 'Data',
      render: (event) => (
        <div>
          <p className="text-gray-900 dark:text-white">
            {new Date(event.createdAt).toLocaleDateString('pt-BR')}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {new Date(event.createdAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
      ),
    },
    {
      key: 'actions',
      label: '',
      align: 'right',
      render: (event) => (
        event.type === 'payment_succeeded' && !event.isRefunded && (
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleRefundClick(event);
            }}
          >
            <Undo2 className="w-4 h-4" />
          </Button>
        )
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/admin/billing')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Eventos de Billing</h1>
            <p className="text-gray-500 dark:text-gray-400 mt-1">
              Historico de pagamentos, assinaturas e reembolsos
            </p>
          </div>
        </div>
        <Select
          value={typeFilter}
          onChange={(e) => {
            setTypeFilter(e.target.value);
            setPage(1);
          }}
          options={[
            { value: 'all', label: 'Todos os eventos' },
            { value: 'payment_succeeded', label: 'Pagamentos aprovados' },
            { value: 'payment_failed', label: 'Pagamentos falhados' },
            { value: 'subscription_created', label: 'Assinaturas criadas' },
            { value: 'subscription_canceled', label: 'Assinaturas canceladas' },
            { value: 'refund', label: 'Reembolsos' },
          ]}
          className="w-56"
        />
      </div>

      {/* Events Table */}
      <Card>
        <DataTable
          data={data?.events || []}
          columns={columns}
          keyExtractor={(event) => event.id}
          isLoading={isLoading}
          emptyMessage="Nenhum evento encontrado"
          pagination={{
            currentPage: page,
            totalPages: data?.totalPages || 1,
            totalItems: data?.total || 0,
            onPageChange: setPage,
          }}
        />
      </Card>

      {/* Refund Modal */}
      <Modal
        isOpen={showRefundModal}
        onClose={() => setShowRefundModal(false)}
        title="Processar Reembolso"
      >
        <div className="space-y-4">
          {selectedEvent && (
            <>
              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-600 dark:text-gray-400">Usuario</span>
                  <span className="font-medium">{selectedEvent.userName}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 dark:text-gray-400">Valor Original</span>
                  <span className="font-medium">{formatCurrency(selectedEvent.amount, selectedEvent.currency)}</span>
                </div>
              </div>

              <Input
                label="Valor do Reembolso"
                type="number"
                step="0.01"
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
                error={parseFloat(refundAmount) > selectedEvent.amount ? 'Valor excede o pagamento original' : undefined}
              />

              <Textarea
                label="Motivo (opcional)"
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
                placeholder="Descreva o motivo do reembolso..."
                rows={3}
              />

              <div className="flex justify-end gap-3 pt-4">
                <Button variant="secondary" onClick={() => setShowRefundModal(false)}>
                  Cancelar
                </Button>
                <Button
                  variant="danger"
                  onClick={handleRefund}
                  isLoading={refundMutation.isPending}
                  disabled={!refundAmount || parseFloat(refundAmount) > selectedEvent.amount}
                >
                  Confirmar Reembolso
                </Button>
              </div>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default BillingEventsPage;
